// ------------------------------------------------------------------------------
// name: useArtistData
// author: mudas
// created: 2024/12/9
// ------------------------------------------------------------------------------

import { useState } from 'react';
import { compact, isEmpty } from 'lodash-es';
import { usePagination } from 'ahooks';

import { ArtistDetailType, SearchData } from 'common/types';
import { FastApiStatusCode } from 'common/service/constants/fastApi';
import service from 'common/service';

export interface PaginationArtistData {
  total: number;
  list: ArtistDetailType[];

  expireTime?: string;
  unConcernNum?: number;
  fromUser?: { uniqueId: number; nickName: string };

  // 该属性由前端处理，根据接口返回 code 是否等于 `FastApiStatusCode.ExpiredSharing` 进行赋值
  expired?: boolean;
}

export const useArtistData = (shareId?: string) => {
  const [values, setValues] = useState<SearchData>();

  const formatParams = (values?: SearchData) => {
    const { orderBy, requirementTypeId, styles, range } = values || {};
    const params: Record<string, any> = {};

    if (orderBy) params.orderBy = orderBy;
    if (requirementTypeId) params.requirementTypeId = requirementTypeId;
    if (!isEmpty(compact(styles))) params.styles = styles;

    if (Array.isArray(range)) {
      params.startTime = range[0].format('YYYY-MM-DD');
      params.endTime = range[1].format('YYYY-MM-DD');
    }

    return params;
  };

  // 创作者列表
  const paginationResult = usePagination(
    async ({ current, pageSize }) => {
      const params = formatParams(values);

      try {
        const { code, data } = await service.artistV2.getConcernsArtistByPage({
          _page: current,
          _num: pageSize,
          shareId,
          ...params,
        });

        return {
          total: data?.total ?? 0,
          list: data?.items ?? [],
          fromUser: data?.fromUser,
          unConcernNum: data?.unConcernNum ?? 0,
          expired: FastApiStatusCode.ExpiredSharing === code,
          expireTime: data?.expireTime,
        } as PaginationArtistData;
      } catch (error) {
        return { total: 0, list: [], expired: false, unConcernNum: 0 } as PaginationArtistData;
      }
    },
    {
      defaultPageSize: 10,
      debounceWait: 200,
      refreshDeps: [values?.orderBy, values?.requirementTypeId, values?.styles, values?.range],
    },
  );

  return { setParams: setValues, ...paginationResult };
};
